import intermind from '../../index';
import { JoinRequestResource } from './resources/join-request';
import { RoomResource } from './resources/room';
import { TranscriptResource } from './resources/transcript';
import { MindSDK, SessionOptions } from 'mind-sdk-web';
import { MediaStream, MediaStreamAudioSupplier, MediaStreamVideoSupplier } from 'mind-sdk-web/lib/MediaStream';
import { Session } from 'mind-sdk-web/lib/Session';
import { ChatResource } from './resources/chat';
import { HistoryResource } from './resources/history';
import { ParticipantResource } from './resources/participant';
import { RaisedHandResource } from './resources/raised-hand';
import { RecordingResource } from './resources/recording';
import { TranslatioResource } from './resources/translation';
import { CommandsResource } from './resources/commands';

export class ConferenceResource {
  private readonly roomResource: RoomResource;
  private readonly joinRequestResource: JoinRequestResource;
  private readonly participantResource: ParticipantResource;
  private readonly recordingResource: RecordingResource;
  private readonly transcriptResource: TranscriptResource;
  private readonly translationResource: TranslatioResource;
  private readonly raisedHandResource: RaisedHandResource;
  private readonly chatResource: ChatResource;
  private readonly historyResource: HistoryResource;
  private readonly commandsResource: CommandsResource;

  constructor() {
    this.roomResource = new RoomResource();
    this.joinRequestResource = new JoinRequestResource();
    this.participantResource = new ParticipantResource();
    this.recordingResource = new RecordingResource();
    this.transcriptResource = new TranscriptResource();
    this.translationResource = new TranslatioResource();
    this.raisedHandResource = new RaisedHandResource();
    this.chatResource = new ChatResource();
    this.historyResource = new HistoryResource();
    this.commandsResource = new CommandsResource();
  }

  async create(): Promise<string> {
    const { data } = await intermind.httpClient.post<{ id: string }>('/rooms');
    return data?.id;
  }

  async delete(conferenceId: string): Promise<void> {
    await intermind.httpClient.delete(`/conferences/${conferenceId}`);
  }

  async join(conferenceUrl: string, participantToken: string): Promise<Session> {
    const options = new SessionOptions();
    options.setUseVp9ForSendingVideo(true);
    return MindSDK.join(conferenceUrl, participantToken, options);
  }

  createMediaStream(
    audioSupplier: MediaStreamAudioSupplier | null,
    videoSupplier: MediaStreamVideoSupplier | null
  ): MediaStream | null {
    try {
      return MindSDK.createMediaStream(audioSupplier, videoSupplier);
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  closeSession(session: Session): void {
    MindSDK.exit2(session);
  }

  get room() {
    return this.roomResource;
  }

  get participant() {
    return this.participantResource;
  }

  get joinRequest() {
    return this.joinRequestResource;
  }

  get recording() {
    return this.recordingResource;
  }

  get transcript() {
    return this.transcriptResource;
  }

  get translation() {
    return this.translationResource;
  }

  get raisedHand() {
    return this.raisedHandResource;
  }

  get chat() {
    return this.chatResource;
  }

  get history() {
    return this.historyResource;
  }

  get commands() {
    return this.commandsResource;
  }
}
